import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChildren,
  HostBinding,
  Input,
  QueryList,
  TemplateRef,
} from '@angular/core';
import {
  DocTimelineTemplateDirective,
  DocTimelineTemplateType,
} from './doc-timeline-template.directive';

export type DocTimelineAlign = 'left' | 'right' | 'alternate' | 'top' | 'bottom';

@Component({
  selector: 'doc-timeline',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="doc-timeline-events">
      @for (event of events; track trackEvent($index, event); let index = $index; let last = $last) {
        <div class="doc-timeline-event">
          @if (align === 'alternate' && index % 2 === 1) {
            <div class="doc-timeline-event-opposite">
              <ng-container
                *ngTemplateOutlet="getTemplate('opposite'); context: templateContext(event, index)">
              </ng-container>
            </div>
          } @else if (align !== 'left' && align !== 'top') {
            <div class="doc-timeline-event-opposite">
              <ng-container
                *ngTemplateOutlet="getTemplate('opposite'); context: templateContext(event, index)">
              </ng-container>
            </div>
          } @else {
            <div class="doc-timeline-event-opposite"></div>
          }

          <div class="doc-timeline-event-separator">
            <div class="doc-timeline-event-marker">
              @if (getTemplate('marker'); as markerTpl) {
                <ng-container
                  *ngTemplateOutlet="markerTpl; context: templateContext(event, index)">
                </ng-container>
              }
            </div>
            @if (!last) {
              <div class="doc-timeline-event-connector"></div>
            }
          </div>

          <div class="doc-timeline-event-content">
            @if (getTemplate('content'); as contentTpl) {
              <ng-container
                *ngTemplateOutlet="contentTpl; context: templateContext(event, index)">
              </ng-container>
            }
          </div>

          @if (align === 'alternate' && index % 2 === 0) {
            <div class="doc-timeline-event-opposite">
              <ng-container
                *ngTemplateOutlet="getTemplate('opposite'); context: templateContext(event, index)">
              </ng-container>
            </div>
          }
        </div>
      }
    </div>
  `,
  styleUrl: './doc-timeline.component.scss',
})
export class DocTimelineComponent {
  @Input() value: readonly unknown[] | null | undefined = [];
  @Input() align: DocTimelineAlign = 'left';
  @Input() styleClass = '';

  @ContentChildren(DocTimelineTemplateDirective)
  private templates!: QueryList<DocTimelineTemplateDirective>;

  @HostBinding('class')
  get hostClasses(): string {
    return [
      'doc-timeline',
      `doc-timeline-${this.align}`,
      this.isHorizontal ? 'doc-timeline-horizontal' : 'doc-timeline-vertical',
      this.styleClass]
      .filter(Boolean)
      .join(' ');
  }

  get events(): readonly unknown[] {
    return this.value ?? [];
  }

  get isHorizontal(): boolean {
    return this.align === 'top' || this.align === 'bottom';
  }

  trackEvent(index: number, event: unknown): unknown {
    return (event as { id?: unknown })?.id ?? index;
  }

  templateContext(event: unknown, index: number): { $implicit: unknown; index: number } {
    return { $implicit: event, index };
  }

  getTemplate(type: DocTimelineTemplateType): TemplateRef<unknown> | null {
    return this.templates?.find((template) => template.type === type)?.template ?? null;
  }
}
